/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Geometry } from 'geojson';
import type {
  Dimension,
  GeographicBoundingBox,
  LayerProps,
} from '@opengeoweb/webmap';
import { getRendersAsStyles } from './utils/stacColormapToOlStyle';

interface Properties {
  datetime: string | null;
  'proj:wkt2'?: string;
}

interface STACAsset {
  href: string;
  type: string;
  created: string;
}

export interface STACExtent {
  spatial?: {
    bbox?: number[][];
  };
  temporal?: {
    interval?: (string | null)[][];
  };
}

export interface STACLink {
  href: string;
  rel: string;
}

export interface STACItem {
  type: 'Feature';
  stac_version: string;
  id: string;
  geometry: Geometry | null;
  bbox: number[];
  properties: Properties;
  links: STACLink[];
  assets: Record<string, STACAsset>;
}

export type STACColormapEntry = [number, number, number, number];

export interface STACRenderEntry {
  title?: string;
  assets: string[];
  rescale?: number[][];
  colormap?: Record<string, STACColormapEntry>;
}

export interface STACCollection {
  type: 'Collection';
  stac_version: string;
  id: string;
  description: string;
  license: string;
  keywords?: string[];
  properties?: Record<string, unknown>;
  extent: STACExtent;
  links: STACLink[];
  features: STACItem[];
  renders?: Record<string, STACRenderEntry>;
}

export const STAC_ITEM_PAGE_LIMIT = 1000;
export const STAC_TEMPORAL_STEP_SAMPLE_LIMIT = 11;
const MAX_STAC_ITEM_PAGES = 1000;

const getStacErrorMessage = async (response: Response): Promise<string> => {
  let details = '';

  try {
    const body = (await response.json()) as {
      error?: string;
      message?: string;
    };
    details = body.message || body.error || JSON.stringify(body);
  } catch {
    // Ignore invalid or empty JSON error bodies.
  }

  return `Failed to fetch STAC resource (${response.status} ${response.statusText})${
    details ? `: ${details}` : ''
  }`;
};

const fetchStacJson = async <T>(
  url: string,
  init?: RequestInit,
): Promise<T> => {
  const response = await fetch(url, init);

  if (!response.ok) {
    throw new Error(await getStacErrorMessage(response));
  }

  return (await response.json()) as T;
};

export const getStacItemsUrl = (
  stacBaseUrlWithCollection: string,
  startDate?: string,
  endDate?: string,
): string => {
  const normalizedUrl = stacBaseUrlWithCollection.trim().replace(/\/+$/, '');
  const searchParams = new URLSearchParams();

  if (startDate && endDate) {
    searchParams.set('datetime', `${startDate}/${endDate}`);
  }

  const queryString = searchParams.toString();
  return `${normalizedUrl}/items${queryString ? `?${queryString}` : ''}`;
};

const getPaginatedStacItemsUrl = (
  baseUrl: string,
  itemLimit: number,
  offset: number,
): string => {
  const url = new URL(baseUrl);
  url.searchParams.set('limit', `${itemLimit}`);
  url.searchParams.set('offset', `${offset}`);
  return url.toString();
};

const getNextLinkUrl = (
  collection: STACCollection,
  currentUrl: string,
): string | undefined => {
  const nextLink = collection.links?.find((link) => link.rel === 'next');
  return nextLink?.href
    ? new URL(nextLink.href, currentUrl).toString()
    : undefined;
};

export const fetchStacCollection = async (
  stacBaseUrlWithCollection: string,
): Promise<STACCollection> => {
  return fetchStacJson<STACCollection>(stacBaseUrlWithCollection);
};

export const fetchStacItem = async (stacItemUrl: string): Promise<STACItem> => {
  return fetchStacJson<STACItem>(stacItemUrl);
};

export const fetchStacItems = async (
  collectionUrl: string,
): Promise<STACItem[]> => {
  const collection = await fetchStacCollection(collectionUrl);
  return (collection.features || []) as STACItem[];
};

export const fetchStacItemsPage = async (
  baseUrl: string,
  itemLimit = STAC_TEMPORAL_STEP_SAMPLE_LIMIT,
  offset = 0,
): Promise<STACItem[]> => {
  const collection = await fetchStacCollection(
    getPaginatedStacItemsUrl(baseUrl, itemLimit, offset),
  );
  return (collection.features || []) as STACItem[];
};

/**
 * Fetches STAC items with pagination support to handle cases where results exceed limit.
 * Uses STAC next links when available, otherwise falls back to offset pagination
 * until a page returns fewer items than the limit.
 *
 * @param baseUrl - Base URL with collection and query params (without offset/limit)
 * @param itemLimit - Max items per page (default: 1000)
 * @returns Array of all fetched STAC items
 */
export const fetchStacItemsWithPagination = async (
  baseUrl: string,
  itemLimit = STAC_ITEM_PAGE_LIMIT,
): Promise<STACItem[]> => {
  const items: STACItem[] = [];
  const visitedUrls = new Set<string>();

  const fetchPage = async (
    requestUrl: string,
    offset: number,
    isUsingNextLinks: boolean,
    page: number,
  ): Promise<STACItem[]> => {
    if (page >= MAX_STAC_ITEM_PAGES) {
      throw new Error(
        `STAC pagination exceeded ${MAX_STAC_ITEM_PAGES} pages for ${baseUrl}`,
      );
    }

    if (visitedUrls.has(requestUrl)) {
      throw new Error(`STAC pagination loop detected for ${requestUrl}`);
    }
    visitedUrls.add(requestUrl);

    const collection = await fetchStacCollection(requestUrl);
    const pageItems = (collection.features || []) as STACItem[];
    items.push(...pageItems);

    const nextUrl = getNextLinkUrl(collection, requestUrl);
    if (nextUrl) {
      return fetchPage(nextUrl, offset, true, page + 1);
    }

    if (isUsingNextLinks) {
      return items;
    }

    if (pageItems.length < itemLimit) {
      return items;
    }

    const nextOffset = offset + itemLimit;
    return fetchPage(
      getPaginatedStacItemsUrl(baseUrl, itemLimit, nextOffset),
      nextOffset,
      false,
      page + 1,
    );
  };

  return fetchPage(
    getPaginatedStacItemsUrl(baseUrl, itemLimit, 0),
    0,
    false,
    0,
  );
};

export interface STACCatalogCollection {
  id: string;
  title?: string;
  description?: string;
  extent?: STACExtent;
  keywords?: string[];
  links: STACLink[];
  renders?: Record<string, STACRenderEntry>;
}

export interface STACCatalogRoot {
  type?: string;
  stac_version?: string;
  id?: string;
  title?: string;
  description?: string;
  links: STACLink[];
  conformsTo?: string[];
}

interface STACCollectionsResponse {
  collections: STACCatalogCollection[];
}

export const fetchStacCatalogRoot = async (
  url: string,
): Promise<STACCatalogRoot> => {
  const requestUrl = url.trim();
  return fetchStacJson<STACCatalogRoot>(requestUrl, {
    headers: {
      Accept: 'application/json',
    },
  });
};

export const fetchStacCatalogCollections = async (
  baseUrl: string,
): Promise<STACCatalogCollection[]> => {
  const normalizedUrl = baseUrl.trim().replace(/\/+$/, '');
  const data = await fetchStacJson<STACCollectionsResponse>(
    `${normalizedUrl}/collections`,
    {
      headers: {
        Accept: 'application/json',
      },
    },
  );

  return data.collections ?? [];
};

const getStacGeographicBoundingBox = (
  extent?: STACExtent,
): GeographicBoundingBox | undefined => {
  const bbox = extent?.spatial?.bbox?.[0];

  if (!bbox?.length) {
    return undefined;
  }

  const [west, south, east, north] =
    bbox.length >= 6
      ? [bbox[0], bbox[1], bbox[3], bbox[4]]
      : [bbox[0], bbox[1], bbox[2], bbox[3]];

  if (![west, south, east, north].every(Number.isFinite)) {
    return undefined;
  }

  return {
    west: `${west}`,
    east: `${east}`,
    north: `${north}`,
    south: `${south}`,
  };
};

const isNonEmptyString = (value?: string | null): value is string =>
  Boolean(value);

const getStacTemporalDimension = (
  extent?: STACExtent,
): Dimension | undefined => {
  const [start, end] = extent?.temporal?.interval?.[0] ?? [];
  const values = [start, end].filter(isNonEmptyString).join('/');

  if (!values) {
    return undefined;
  }

  return {
    name: 'time',
    units: 'ISO8601',
    currentValue: start ?? end ?? '',
    values,
  };
};

export const stacCatalogCollectionToLayerProps = (
  collection: STACCatalogCollection,
): LayerProps => {
  const geographicBoundingBox = getStacGeographicBoundingBox(collection.extent);
  const temporalDimension = getStacTemporalDimension(collection.extent);
  const styles = collection.renders
    ? getRendersAsStyles(collection.renders)
    : undefined;

  return {
    name: collection.id,
    title: collection.title ?? collection.id,
    abstract: collection.description ?? '',
    leaf: true,
    path: [],
    keywords: collection.keywords ?? [],
    ...(geographicBoundingBox && { geographicBoundingBox }),
    ...(temporalDimension && { dimensions: [temporalDimension] }),
    ...(styles?.length && { styles }),
  };
};
